import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import Cookies from 'universal-cookie';
import emailSpellChecker from '@zootools/email-spell-checker';

import { useAuth } from '../../hooks/use-auth';
import * as usersService from '../../services/api/users';
import StyledInput from '../common/form/styled-input';
import { strings, parameters } from '../../localization/strings';
import StyledLabel from '../common/form/styled-label';
import Heading2 from '../common/headings/heading2';
import StyledError from '../common/form/styled-error';
import CommonAnimatedLoader from '../common/animated/loader';
import CommonButtonDefault from '../common/buttons/default';
import { ModalKeys } from '../../context/modals';
import { useModal } from '../../hooks/use-modal';
import { COOKIE_NAME_ANSWER } from '../../context/questionnaire';
import * as questionnaireApi from '../../services/api/questionnaire';
import { useSite } from '../../hooks/use-site';
import AlertPrimary from '../common/alerts/alert-primary';
import { getLocalizedPath } from '../../helpers/localization-helper';
import { pushGTMEvent, GTM_EVENTS } from '../../utils/gtm-events';
import EmailSuggestion from '../common/form/email-suggestion';

interface ComponentProps {
  isQuestionnaire?: boolean;
  orderHash?: string;
  onLoadingChange?: (isLoading: boolean) => void;
}

type Inputs = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  agreeMarketing: boolean;
  agreeResearch: boolean;
  agreeTerms: boolean;
  agreeRemoteConsultation: boolean;
  language: string;
};

function UsersRegisterFormComponent({ isQuestionnaire, orderHash, onLoadingChange }: ComponentProps) {
  const { setUserToken } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { setVisible } = useModal();
  const [allChecked, setAllChecked] = useState(false);

  const [firstName, setFirstName] = useState<string>();
  const cookies = new Cookies();

  const hash = cookies.get(COOKIE_NAME_ANSWER);

  const { hasConsultation, isSelfCo, locale, region } = useSite();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    setValue,
  } = useForm<Inputs>({
    defaultValues: {
      language: locale,
    },
  });
  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    try {
      setIsLoading(true);
      const token = await usersService.register({
        ...data,
        email: data.email.trim(),
        orderHash,
        region: isSelfCo && region ? region : undefined,
      });

      await setUserToken(token, false);

      pushGTMEvent(GTM_EVENTS.COMPLETE_REGISTRATION);

      if (isQuestionnaire) {
        if (isSelfCo) {
          navigate(getLocalizedPath(parameters().shipment, locale, region));
        } else {
          navigate(getLocalizedPath(parameters().selectPatient, locale, region));
        }
      } else {
        navigate(getLocalizedPath(parameters().dashboard, locale, region));
      }
    } catch (e) {
      setIsLoading(false);
      if (e instanceof Error) {
        setError('email', { message: e.message });
      }
    }
  };

  useEffect(() => {
    const fetchAnswer = async (h: string) => {
      try {
        setIsLoading(true);
        const res = await questionnaireApi.answer(h);

        if (res.customerName) {
          setFirstName(res.customerName);
          setValue('firstName', res.customerName);
        }
        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
        setErrorMessage((e as Error).message);
      }
    };

    if (hash) {
      fetchAnswer(hash);
    }
  }, [hash, setValue]);

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoading);
    }
  }, [isLoading, onLoadingChange]);

  const onOpenModal = (key: string) => {
    setVisible(key, true);
  };

  const updateCheckbox = () => {
    const newValue = !allChecked;

    setAllChecked(newValue);
    setValue('agreeTerms', newValue);
    setValue('agreeResearch', newValue);
    setValue('agreeMarketing', newValue);
    if (hasConsultation) {
      setValue('agreeRemoteConsultation', newValue);
    }
  };

  const handleEmailChange = () => {
    setEmailSuggestion(null);
  };

  const handleEmailBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const originalEmail = event.target.value;
    let email = originalEmail;

    if (email) {
      const wasYaho = /@yaho\./i.test(email);

      email = email.replace(/@yaho\./i, '@yahoo.');

      const additionalDomains = [
        'wp.pl',
        'o2.pl',
        'inbox.lt',
        'interia.pl',
        'op.pl',
        'mail.ru',
        'onet.pl',
        'vp.pl',
        'poczta.onet.pl',
        'tlen.pl',
        'onet.eu',
        'imunodiagnostika.lt',
        'poczta.fm',
        'gazeta.pl',
        'ymail.com',
      ];

      const localeTLDs = [];

      if (locale === 'pl') {
        localeTLDs.push('pl');
      } else if (locale === 'lv') {
        localeTLDs.push('lv');
      } else if (locale === 'lt') {
        localeTLDs.push('lt');
      } else if (locale === 'de') {
        localeTLDs.push('de');
      }

      const suggestion = emailSpellChecker.run({
        email,
        domains: [...emailSpellChecker.POPULAR_DOMAINS, ...additionalDomains],
        ...(localeTLDs.length > 0 && { secondLevelDomains: localeTLDs }),
        topLevelThreshold: 3,
        domainThreshold: 3,
      });

      if (suggestion) {
        let depth = 0;
        const maxDepth = 5;
        let currentEmail = suggestion.full;

        while (depth < maxDepth) {
          const check = emailSpellChecker.run({
            email: currentEmail,
            domains: [...emailSpellChecker.POPULAR_DOMAINS, ...additionalDomains],
            topLevelThreshold: 3,
            domainThreshold: 3,
          });

          if (!check) {
            const finalEmail = currentEmail.replace(/@yahoo\.co$/i, '@yahoo.com');

            setEmailSuggestion(finalEmail);
            break;
          }

          currentEmail = check.full;
          depth += 1;
        }
      } else if (wasYaho && email !== originalEmail) {
        const finalEmail = email.replace(/@yahoo\.co$/i, '@yahoo.com');

        setEmailSuggestion(finalEmail);
      }
    }
  };

  const acceptEmailSuggestion = () => {
    if (emailSuggestion) {
      setValue('email', emailSuggestion);
      setEmailSuggestion(null);
    }
  };

  const dismissEmailSuggestion = () => {
    setEmailSuggestion(null);
  };

  return isLoading ? (
    <CommonAnimatedLoader />
  ) : (
    <div>
      <Heading2 className="mb-6">{strings().registration.title}</Heading2>
      <div className="md:w-9/12 mb-6 md:mb-10 my-5">{strings().registration.text}</div>
      <form onSubmit={handleSubmit(onSubmit)}>
        {errorMessage && (
          <div className="container max-w-4xl mx-auto mt-3">
            <AlertPrimary type="danger" title={strings().error.common.title} />
          </div>
        )}

        <div>
          <StyledLabel htmlFor="name">
            {strings().form.name}
            <span className="text-primary">*</span>
          </StyledLabel>
          <StyledInput
            {...register('firstName', { required: true })}
            fullWidth
            id="firstName"
            name="firstName"
            defaultValue={firstName}
            autoComplete="given-name"
            className={` ${errors.firstName && `!border-red-500`} `}
            type="text"
          />
          {errors.firstName && <StyledError>{strings().error.fieldIsRequired}</StyledError>}
        </div>
        <div className="mt-5">
          <StyledLabel>
            {strings().form.surname}
            <span className="text-primary">*</span>
          </StyledLabel>
          <StyledInput
            fullWidth
            className={` ${errors.lastName && `!border-red-500`} `}
            type="text"
            autoComplete="family-name"
            {...register('lastName', { required: true })}
          />
          {errors.lastName && <StyledError>{strings().error.fieldIsRequired}</StyledError>}
        </div>

        <div className="mt-5">
          <StyledLabel>
            {strings().form.email}
            <span className="text-primary">*</span>
          </StyledLabel>
          <StyledInput
            fullWidth
            className={` ${errors.email && `!border-red-500`} `}
            type="email"
            autoComplete="email"
            {...register('email', {
              required: true,
              onChange: handleEmailChange,
            })}
            onBlur={handleEmailBlur}
          />
          {errors.email && (
            <StyledError>
              {errors.email.message ? strings().error.email.isUsed : strings().error.email.required}
            </StyledError>
          )}
          {emailSuggestion && (
            <EmailSuggestion
              suggestedEmail={emailSuggestion}
              onAccept={acceptEmailSuggestion}
              onDismiss={dismissEmailSuggestion}
            />
          )}
        </div>
        {!isSelfCo && (
          <div className="mt-5">
            <StyledLabel htmlFor="phone">
              {strings().form.phone}
              <span className="text-primary">*</span>
            </StyledLabel>
            <StyledInput
              fullWidth
              type="tel"
              id="phone"
              inputMode="tel"
              autoComplete="tel"
              className={` ${errors.phone && `!border-red-500`} `}
              {...register('phone', {
                required: !isSelfCo,
              })}
            />
            {errors.phone && <StyledError>{strings().error.phone.required}</StyledError>}
          </div>
        )}
        <div className="mt-5">
          <StyledLabel>
            {strings().form.password}
            <span className="text-primary">*</span>
          </StyledLabel>
          <StyledInput
            fullWidth
            className={` ${errors.password && `!border-red-500`} `}
            type="password"
            {...register('password', { required: true, minLength: parameters().password.minLength })}
          />
          {errors.password && (
            <StyledError>
              {errors.password.type === 'required' && strings().error.fieldIsRequired}
              {errors.password.type === 'minLength' &&
                strings().error.password.minLength.replace('{length}', `${parameters().password.minLength}`)}
            </StyledError>
          )}
          <div className="text-gray-400 text-sm mt-2">{strings().form.atLeastEightSymbols}</div>
        </div>

        <div className="mt-5">
          <div className="flex items-center">
            <input
              className="h-4 w-4 text-blue-900 bg-white border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-0 focus:border-gray-300 checked:border-transparent"
              id="agreeTerms"
              type="checkbox"
              onClick={() => updateCheckbox()}
            />
            <label htmlFor="agreeTerms" className="ml-2 block text-sm align-middle leading-6 text-gray-900">
              <strong>{strings().checkout.checkBoxAgreeAll}</strong>
            </label>
          </div>
        </div>

        <div className="mt-5">
          <div className="flex items-center">
            <input
              className="h-4 w-4 text-blue-900 bg-white border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-0 focus:border-gray-300 checked:border-transparent"
              id="agreeTerms"
              type="checkbox"
              {...register('agreeTerms', { required: true })}
            />
            <label htmlFor="agreeTerms" className="ml-2 block text-sm align-middle leading-6 text-gray-900">
              {strings().checkout.checkboxIHaveReadAnd}{' '}
              <CommonButtonDefault className="!text-sm" link onClick={() => onOpenModal(ModalKeys.Privacy)}>
                {strings().checkout.checkboxPrivacyPolicy}
              </CommonButtonDefault>{' '}
              {strings().checkout.checkboxAnd}{' '}
              <CommonButtonDefault className="!text-sm" link onClick={() => onOpenModal(ModalKeys.Terms)}>
                {strings().checkout.checkBoxTermsOfService}
              </CommonButtonDefault>
            </label>
          </div>
        </div>
        {errors.agreeTerms && <StyledError>{strings().error.agreeTerms.required}</StyledError>}

        <div className="flex items-center mt-3">
          <input
            className="h-4 w-4 text-blue-900 bg-white border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-0 focus:border-gray-300 checked:border-transparent"
            id="agreeResearch"
            type="checkbox"
            {...register('agreeResearch', { required: true })}
          />
          <label htmlFor="agreeResearch" className="ml-2 block text-sm align-middle leading-6 text-gray-900">
            {strings().checkout.checkboxIAgreeThatImunodiagnostika}
          </label>
        </div>
        {errors.agreeResearch && <StyledError>{strings().error.required}</StyledError>}

        {hasConsultation && (
          <div className="mt-5">
            <div className="flex space-x-2">
              <div className="flex items-center h-5">
                <input
                  {...register('agreeRemoteConsultation', { required: true })}
                  id="agreeRemoteConsultation"
                  name="agreeRemoteConsultation"
                  type="checkbox"
                  className="h-4 w-4 text-blue-900 bg-white border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-0 focus:border-gray-300 checked:border-transparent"
                />
              </div>
              <label htmlFor="agreeRemoteConsultation" className="text-sm text-gray-900">
                {strings().checkout.checkboxAgreeRemoteConsultation}
                <CommonButtonDefault
                  type="button"
                  className="!text-sm"
                  link
                  onClick={() => onOpenModal(ModalKeys.RemoteConsultation)}
                >
                  {strings().checkout.checkboxAgreeRemoteConsultationSuffix}
                </CommonButtonDefault>
              </label>
            </div>
            {errors.agreeRemoteConsultation && (
              <StyledError>{strings().error.agreeRemoteConsultation.required}</StyledError>
            )}
          </div>
        )}

        <div className="flex items-center mt-3">
          <div className="flex items-center h-5">
            <input
              className="h-4 w-4 text-blue-900 bg-white border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-0 focus:border-gray-300 checked:border-transparent"
              id="agreeMarketing"
              type="checkbox"
              {...register('agreeMarketing', {})}
            />
          </div>
          <label htmlFor="agreeMarketing" className="ml-2 text-sm text-gray-900">
            {strings().checkout.checkboxIAgreeToGetDoctorAdvice}
          </label>
        </div>
        <div className="mt-5 md:mt-7 flex justify-end">
          <CommonButtonDefault primary type="submit">
            {strings().button.createAccount}
          </CommonButtonDefault>
        </div>
        <div
          className="flex justify-end text-end mt-3"
          dangerouslySetInnerHTML={{ __html: strings().checkout.dataIsSecured }}
        />
      </form>
    </div>
  );
}

UsersRegisterFormComponent.defaultProps = {
  isQuestionnaire: false,
  orderHash: '',
  onLoadingChange: undefined,
};

export default UsersRegisterFormComponent;
